"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Panel = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const lucide_1 = require("lucide");
const event_1 = require("@/common/event");
const ui_1 = require("@/ui/ui");
const buttonGroup_1 = require("@/ui/button/buttonGroup");
const switcher_1 = require("@/ui/switcher/switcher");
const modalProvider_1 = require("@/ui/modal/modalProvider");
const contextMenuProvider_1 = require("@/ui/contextMenu/contextMenuProvider");
const global_1 = require("@/common/global");
const settings_1 = require("@/common/settings");
const i18n_1 = require("@/common/i18n");
require("./panel.less");
const defaultOptions = {};
class Panel extends ui_1.Component {
    constructor(target, _options) {
        super((0, jsx_runtime_1.jsx)("div", { className: "panel" }), target, defaultOptions, _options);
        this._renderer = null;
        // events
        this._onSelectedObjectChange = new event_1.Emitter();
        this._switchers = [];
        this._modalAutoPause = false;
        // UI
        const toolbar = (0, ui_1.createElement)("div", this);
        toolbar.classList.add("panel-toolbar");
        const leftSplit = (0, ui_1.createElement)("div", toolbar);
        leftSplit.classList.add("panel-toolbar-left-split");
        const toolbarLeftGroup = new buttonGroup_1.ButtonGroup(leftSplit);
        toolbarLeftGroup.addButton({ icon: lucide_1.Settings, tooltip: (0, i18n_1.$)("panel.tooltip.settings") }, () => modalProvider_1.modalProvider.open("settings"));
        toolbarLeftGroup.addButton({ icon: lucide_1.Box, tooltip: (0, i18n_1.$)("panel.tooltip.manager") }, () => modalProvider_1.modalProvider.open("manager", { objects: this._renderer.getObjects() }));
        toolbarLeftGroup.addSwitcher({ icon: lucide_1.MousePointer2, tooltip: (0, i18n_1.$)("panel.tooltip.mouse-mode") }, ({ isActive }) => this._renderer.setMouseMode(isActive));
        this._wallModeSwitcher = toolbarLeftGroup.addSwitcher({ icon: lucide_1.BrickWall, tooltip: (0, i18n_1.$)("panel.tooltip.wall"), defaultValue: settings_1.Settings.get().getValue("wallMode") }, ({ isActive }) => settings_1.Settings.get().setValue("wallMode", isActive));
        this._refreshButton = toolbarLeftGroup.addButton({ icon: lucide_1.RotateCw, tooltip: (0, i18n_1.$)("panel.tooltip.refresh") });
        this._pauseSwitcher = toolbarLeftGroup.addSwitcher({ icon: lucide_1.Pause, tooltip: (0, i18n_1.$)("panel.tooltip.pause") }, ({ isActive }) => {
            isActive ? this._pauseRenderer() : this._unpauseRenderer();
        });
        const rightSplit = (0, ui_1.createElement)("div", toolbar);
        rightSplit.classList.add("panel-toolbar-right-split");
        this._fpsLabel = (0, ui_1.createElement)("span", rightSplit);
        const versionLabel = (0, ui_1.createElement)("span", rightSplit);
        versionLabel.textContent = (0, global_1.getVersionString)();
        const toolbarRightGroup = new buttonGroup_1.ButtonGroup(rightSplit);
        toolbarRightGroup.addButton({ icon: lucide_1.Share, tooltip: (0, i18n_1.$)("panel.tooltip.export") }, () => { });
        toolbarRightGroup.addButton({ icon: lucide_1.FilePlus2, tooltip: (0, i18n_1.$)("panel.tooltip.import") }, () => modalProvider_1.modalProvider.open("import", { renderer: this._renderer }));
        toolbarRightGroup.addButton({ icon: lucide_1.Info, tooltip: (0, i18n_1.$)("panel.tooltip.about"), tooltipPosition: "top-left" }, () => modalProvider_1.modalProvider.open("about"));
        const switcherContainer = (0, ui_1.createElement)("div", this);
        switcherContainer.classList.add("panel-switcher-container");
        // Context Menu
        contextMenuProvider_1.contextMenuProvider.registerContextMenu(this, [
            {
                text: (0, i18n_1.$)("panel.ctx.refresh"),
                icon: lucide_1.RotateCw,
                action: () => {
                    if (this._renderer.isPaused)
                        return;
                    this._renderer.refresh();
                }
            },
            { separator: true },
            {
                text: (0, i18n_1.$)("panel.ctx.settings"),
                icon: lucide_1.Settings,
                action: () => modalProvider_1.modalProvider.open("settings")
            },
            {
                text: (0, i18n_1.$)("panel.ctx.manager"),
                icon: lucide_1.Box,
                action: () => modalProvider_1.modalProvider.open("manager")
            },
            {
                text: (0, i18n_1.$)("panel.ctx.about"),
                icon: lucide_1.Info,
                action: () => modalProvider_1.modalProvider.open("about")
            }
        ]);
        this._register(settings_1.Settings.get().onDidChange(({ key, value }) => {
            if (key === "wallMode") {
                this._wallModeSwitcher.setActive(value);
            }
        }));
        document.body.addEventListener("keydown", (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
                return;
            const code = e.key.charCodeAt(0);
            if (code >= 48 && code <= 57) { // 0-9
                const index = parseInt(e.key, 10) - 1;
                if (index >= 0 && index < this._switchers.length) {
                    const switcher = this._switchers[index];
                    if (!switcher.disabled)
                        switcher.select();
                }
            }
        });
    }
    _pauseRenderer() {
        this._renderer.pause();
        this._pauseSwitcher.setIcon(lucide_1.Play);
        this._pauseSwitcher.setTooltip((0, i18n_1.$)("panel.tooltip.unpause"));
        this._refreshButton.disabled = true;
    }
    _unpauseRenderer() {
        this._renderer.unpause();
        this._pauseSwitcher.setIcon(lucide_1.Pause);
        this._pauseSwitcher.setTooltip((0, i18n_1.$)("panel.tooltip.pause"));
        this._refreshButton.disabled = false;
    }
    linkRenderer(renderer) {
        this._renderer = renderer;
        this._register(this._renderer);
        this._register(this._renderer.onRender(({ fps }) => {
            if (!settings_1.Settings.get().getValue("fps")) {
                this._fpsLabel.textContent = "";
                return;
            }
            this._fpsLabel.textContent = `FPS: ${fps.toFixed(2)}`;
        }));
        this._register(this._refreshButton.onClick(() => {
            this._renderer.refresh();
        }));
        this._register(modalProvider_1.modalProvider.onModalOpen(() => {
            if (!this._renderer.isPaused) {
                this._pauseRenderer();
                this._modalAutoPause = true;
            }
        }));
        this._register(modalProvider_1.modalProvider.onModalClose(() => {
            if (this._modalAutoPause) {
                this._unpauseRenderer();
                this._modalAutoPause = false;
            }
        }));
        for (const switcher of this._switchers) {
            this._register(switcher.onDidChange(({ id, isActive }) => {
                this._onSelectedObjectChange.fire(id.replace("btn.obj.", ""));
                if (!isActive) {
                    switcher.setActive(true);
                }
                for (const _switcher of this._switchers) {
                    if (_switcher.id !== id) {
                        _switcher.setActive(false);
                    }
                }
            }));
        }
    }
    addObjectSwitcher(id, displayName, icon, disabled = false, defaultValue = false) {
        const switcherContainer = document.querySelector(".panel-switcher-container");
        const switcher = new switcher_1.Switcher(switcherContainer, {
            id: "btn.obj." + id,
            text: displayName,
            icon,
            disabled,
            defaultValue,
            contextMenuItems: [
                {
                    text: (0, i18n_1.$)("panel.button.ctx.select"),
                    action: () => switcher.select()
                }
            ]
        });
        this._switchers.push(switcher);
        return switcher;
    }
    get onSelectedObjectChange() {
        return this._onSelectedObjectChange.event;
    }
}
exports.Panel = Panel;
